import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dog, Trash2, Loader2, Search, AlertTriangle, 
  RotateCcw, Eye, EyeOff, ChevronDown, ChevronUp,
  User, Calendar, Building2
} from 'lucide-react';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function AdminDogs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [dogs, setDogs] = useState([]);
  const [deletedDogs, setDeletedDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/logowanie');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Brak dostępu - tylko admin');
        navigate('/panel');
        return;
      }
      fetchAllDogs();
    }
  }, [user, authLoading, navigate]);

  const fetchAllDogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get(`${API}/admin/dogs/all`, { headers });
      
      const active = (res.data || []).filter(d => !d.is_deleted);
      const deleted = (res.data || []).filter(d => d.is_deleted);
      
      setDogs(active);
      setDeletedDogs(deleted);
    } catch (e) {
      console.error('Error fetching dogs:', e);
      toast.error('Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal || !canDelete) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/dogs/${deleteModal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Move to deleted list
      setDogs(prev => prev.filter(d => d.id !== deleteModal.id));
      setDeletedDogs(prev => [...prev, { ...deleteModal, is_deleted: true, deleted_at: new Date().toISOString() }]);
      
      toast.success(`Pies "${deleteModal.name}" usunięty`);
      setDeleteModal(null);
      setConfirmText('');
    } catch (e) {
      toast.error('Błąd usuwania');
    } finally {
      setProcessing(false);
    }
  };

  // Check if user can delete (accepts USUŃ, USUN, usun, usuń in any form)
  const normalizedText = confirmText.trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/ń/g, 'n'); // Handle direct ń character
  const canDelete = normalizedText === 'usun' || 
                    confirmText.trim().toLowerCase() === 'usuń' ||
                    confirmText.trim().toUpperCase() === 'USUŃ';

  const handleRestore = async () => {
    if (!restoreModal) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/dogs/${restoreModal.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Move back to active list
      setDeletedDogs(prev => prev.filter(d => d.id !== restoreModal.id));
      setDogs(prev => [...prev, { ...restoreModal, is_deleted: false }]);
      
      toast.success(`Pies "${restoreModal.name}" przywrócony`);
      setRestoreModal(null);
    } catch (e) {
      toast.error('Błąd przywracania');
    } finally {
      setProcessing(false);
    }
  };

  const filteredDogs = dogs.filter(d => 
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.breed?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDeletedDogs = deletedDogs.filter(d => 
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.breed?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <PanelLayout title="Admin - Psy">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout title="Zarządzanie psami">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Zarządzanie psami</h1>
            <p className="text-zinc-500">Panel administracyjny - soft delete i przywracanie</p>
          </div>
          <Badge className="bg-red-100 text-red-700 border-red-200">
            Admin Only
          </Badge>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Szukaj po nazwie lub rasie..."
            className="pl-10 h-12 bg-white border-zinc-200"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Dog className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-zinc-900">{dogs.length}</p>
                <p className="text-sm text-zinc-500">Aktywnych psów</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-zinc-900">{deletedDogs.length}</p>
                <p className="text-sm text-zinc-500">Usuniętych</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Dogs */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <div className="p-4 border-b border-zinc-200 bg-zinc-50">
            <h2 className="font-semibold text-zinc-900">Aktywne psy ({filteredDogs.length})</h2>
          </div>
          
          {filteredDogs.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              {searchQuery ? 'Nie znaleziono psów' : 'Brak aktywnych psów'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredDogs.map(dog => (
                <div key={dog.id} className="p-4 flex items-center gap-4 hover:bg-zinc-50">
                  <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {dog.main_photo || dog.cover_image_url ? (
                      <img 
                        src={dog.main_photo || dog.cover_image_url} 
                        alt={dog.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Dog className="w-6 h-6 text-zinc-400" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-zinc-900 truncate">{dog.name}</p>
                    <p className="text-sm text-zinc-500 truncate">{dog.breed}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <User className="w-3 h-3" />
                    <span className="truncate max-w-[100px]">{dog.user_id?.slice(0, 8)}...</span>
                  </div>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteModal(dog)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Usuń
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deleted Dogs */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className="w-full p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between hover:bg-zinc-100 transition-colors"
          >
            <h2 className="font-semibold text-zinc-900">Usunięte psy ({filteredDeletedDogs.length})</h2>
            {showDeleted ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          <AnimatePresence>
            {showDeleted && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                {filteredDeletedDogs.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    Brak usuniętych psów
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredDeletedDogs.map(dog => (
                      <div key={dog.id} className="p-4 flex items-center gap-4 hover:bg-zinc-50 opacity-60">
                        <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {dog.main_photo || dog.cover_image_url ? (
                            <img 
                              src={dog.main_photo || dog.cover_image_url} 
                              alt={dog.name}
                              className="w-full h-full object-cover grayscale"
                            />
                          ) : (
                            <Dog className="w-6 h-6 text-zinc-400" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-zinc-900 truncate">{dog.name}</p>
                          <p className="text-sm text-zinc-500 truncate">{dog.breed}</p>
                          {dog.deleted_at && (
                            <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                              <Calendar className="w-3 h-3" />
                              Usunięto: {new Date(dog.deleted_at).toLocaleDateString('pl-PL')}
                            </p>
                          )}
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={() => setRestoreModal(dog)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Przywróć
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-zinc-900">Usuń psa</h3>
                <p className="text-sm text-zinc-500">{deleteModal.name}</p>
              </div>
            </div>
            
            <p className="text-zinc-600 mb-4">
              Pies zostanie oznaczony jako usunięty i ukryty z publicznych stron. 
              Możesz go później przywrócić.
            </p>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-zinc-700 mb-1.5 block">
                Wpisz <span className="font-bold text-red-600">USUŃ</span> aby potwierdzić
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="USUŃ"
                className="border-zinc-300"
              />
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => { setDeleteModal(null); setConfirmText(''); }}
                className="flex-1"
                disabled={processing}
              >
                Anuluj
              </Button>
              <Button 
                onClick={handleDelete}
                disabled={!canDelete || processing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Usuń
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Restore Modal */}
      {restoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-zinc-900">Przywróć psa</h3>
                <p className="text-sm text-zinc-500">{restoreModal.name}</p>
              </div>
            </div>
            
            <p className="text-zinc-600 mb-6">
              Pies zostanie przywrócony i będzie ponownie widoczny na publicznych stronach.
            </p>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setRestoreModal(null)}
                className="flex-1"
                disabled={processing}
              >
                Anuluj
              </Button>
              <Button 
                onClick={handleRestore}
                disabled={processing}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Przywróć
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </PanelLayout>
  );
}
